import Typography from "@mui/material/Typography";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { affiliateIndustries } from "./HomeSharedElements";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import { getUrls } from "@portal/app/_common/lib/urls";
import { paletteOptions } from "@portal/app/_components/theme/options/palette";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { HomepageHeaderTypography } from "@portal/app/HomePage";
import heroGroup from "@portal/app/_components/resources/images/homepage_hero_group.webp";

export const HomeHeroSection = ({
  zip,
  shouldPromptForLocation = false,
}: {
  zip: string;
  shouldPromptForLocation?: boolean;
}) => {
  const urls = getUrls();
  const analytics = useMixpanel();

  return (
    <Section>
      <HeroTextCenter>
        <Typography className="hero_eyebrow">
          Find programs that fit your life, timeline, and dreams.
        </Typography>
        <HomepageHeaderTypography
          component="h1"
          className="title"
          sx={{ color: paletteOptions.primary.main }}
          data-testid="home-header"
        >
          Education for any future
        </HomepageHeaderTypography>
        <FindMyMatchCta
          href={urls.FormFunnelHome()}
          variant="contained"
          color="primary"
          size="large"
          endIcon={<FontAwesomeIcon icon={faArrowRight} />}
          onClick={() =>
            analytics.track("Homepage - Clicked Find My Match", { zip, surface: "hero" })
          }
          data-testid="home-hero-find-my-match-cta"
        >
          Find My Match
        </FindMyMatchCta>
        <div className="search_by_industry">
          <div className="search_by_industry_text_container">
            <Typography className="search_by_industry_text">Search by industry</Typography>
            <FontAwesomeIcon icon={faChevronRight} />
          </div>
          <div className="search_by_industry_buttons">
            {affiliateIndustries.map((i) => (
              <IndustryPill
                key={i.industry}
                href={urls.DreamSearch({
                  industry: i.industry,
                  zip,
                  shouldPromptForLocation,
                })}
                onClick={() =>
                  analytics.track("Homepage - Navigate to DreamSearch", {
                    industry: i.industry,
                    zip,
                  })
                }
                variant="outlined"
                data-testid={`home-hero-industry-pill-${i.industry.toLowerCase()}`}
              >
                {i.label}
              </IndustryPill>
            ))}
          </div>
        </div>
      </HeroTextCenter>
      <HeroImage>
        <img src={heroGroup.src} alt="Dreambound students" className="hero_img" width={800} height={500} />
      </HeroImage>
    </Section>
  );
};

const Section = styled("section")`
  padding: 8rem 1rem 0;
  width: 100%;
  max-width: 75rem;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;

  p {
    color: ${paletteOptions.primary.main};
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: 7rem 1rem 0;
  }
`;

const HeroTextCenter = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 50rem;

  .hero_eyebrow {
    font-size: 1rem;
    opacity: 0.9;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  .title {
    margin: 1rem 0;
    text-align: center;
    font-size: 3.75rem;
    line-height: 1.05;
  }

  .search_by_industry {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
    margin-top: 2rem;

    .search_by_industry_text_container {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .search_by_industry_text {
        font-weight: bold;
      }

      svg {
        color: ${paletteOptions.primary.main};
      }
    }

    .search_by_industry_buttons {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      justify-content: center;
    }
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    .title {
      font-size: 2.5rem;
    }
  }
`;

const HeroImage = styled("div")`
  margin-top: 2.5rem;
  width: 100%;
  max-width: 50rem;
  padding: 0 1rem;

  .hero_img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    max-width: 100%;
    padding: 0;
  }
`;

// Border beam: a square ::before larger than the button rotates via transform.
// We tried the typical @property-driven conic-gradient angle pattern first, but
// the registered angle interpolated correctly in CSSOM while the painted gradient
// stayed stale — a known invalidation issue on some browser/GPU combinations.
// transform animations are driven by the compositor every frame and don't depend
// on paint invalidation, so the rotation is reliably visible. ::after fills the
// inner area with the button background, leaving only a thin ring around the
// perimeter where the rotating gradient shows through.
const FindMyMatchCta = styled(Button)`
  @keyframes find-my-match-border-beam {
    to {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }

  /* No border-radius declared here on purpose. MUI's theme override —
     .MuiButton-root:not(.MuiButton-text):not(.MuiButtonGroup-grouped) (specificity
     0,3,0) — beats this styled component's &.MuiButton-root selector (0,2,0), so
     any value declared here is dead code. The button has always rendered at the
     theme's 0.5rem regardless of what was written here. ::after's border-radius:
     inherit picks up that same theme value, keeping the inner shape concentric
     with the parent's actual rendered radius. */
  &.MuiButton-root {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    padding: 0.875rem 2rem;
    font-size: 1.125rem;
    font-weight: 700;
    text-transform: none;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);

    &:focus-visible {
      outline: 2px solid ${paletteOptions.primary.main};
      outline-offset: 2px;
    }

    .MuiButton-endIcon {
      margin-left: 0.75rem;
    }

    /* Rotating gradient surface (large square that overflows the button). */
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 250%;
      aspect-ratio: 1;
      z-index: -2;
      background: conic-gradient(
        from 0deg,
        transparent 0deg,
        rgba(122, 156, 255, 0.4) 60deg,
        rgba(180, 200, 255, 0.85) 80deg,
        rgba(255, 255, 255, 1) 90deg,
        rgba(180, 200, 255, 0.85) 100deg,
        rgba(122, 156, 255, 0.4) 120deg,
        transparent 180deg,
        transparent 360deg
      );
      transform: translate(-50%, -50%) rotate(0deg);
      animation: find-my-match-border-beam 3s linear infinite;
      pointer-events: none;
    }

    /* Inner cover: leaves a 3px ring around the perimeter where ::before shows.
       background-color: inherit lets MUI's hover/focus background changes
       still tint the button center. */
    &::after {
      content: "";
      position: absolute;
      inset: 3px;
      z-index: -1;
      border-radius: inherit;
      background-color: inherit;
      pointer-events: none;
    }

    @media (prefers-reduced-motion: reduce) {
      &::before {
        animation: none;
        background: conic-gradient(
          rgba(122, 156, 255, 0.6),
          rgba(255, 255, 255, 0.85),
          rgba(122, 156, 255, 0.6),
          rgba(255, 255, 255, 0.85),
          rgba(122, 156, 255, 0.6)
        );
      }
    }
  }
`;

const IndustryPill = styled(Button)`
  &.MuiButton-root {
    border-radius: 2.5rem;
    height: 2.75rem;
    color: ${paletteOptions.primary.main};
    background: white;
    border: none;

    &:hover {
      background: rgba(255, 255, 255, 0.85);
    }

    &:focus-visible {
      outline: 2px solid ${paletteOptions.primary.main};
      outline-offset: 2px;
    }
  }
`;
